import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import type { WorkflowFailure } from '../types/workflow-inspection.types';

import { FailureChain } from './failure-chain';
import { JsonViewer } from './json-viewer';

interface Props {
  /** Decoded workflow input (positional args); `undefined` = not available. */
  input: unknown[] | null | undefined;
  result: unknown;
  failure: WorkflowFailure | null | undefined;
  isRunning: boolean;
  /** True when result/failure come from the saved run record, not Temporal. */
  fromRunRecord?: boolean;
}

/**
 * Temporal's Input | Result pair. Result shows the failure chain (plus its
 * JSON) for a failed run, the result JSON on success, and a "still running"
 * note while open. When Temporal history has expired, the caller passes the
 * persisted run result/failure instead and the panel says so.
 */
export function InputResultPanels({
  input,
  result,
  failure,
  isRunning,
  fromRunRecord = false,
}: Props) {
  const { t } = useTranslation('workflow-inspection');
  const hasResult = result !== null && result !== undefined;

  return (
    <div
      className="grid grid-cols-1 gap-4 lg:grid-cols-2"
      data-testid="workflow-inspector-input-result"
    >
      <Panel title={t('panels.input')}>
        {input === undefined ? null : input && input.length > 0 ? (
          <JsonViewer
            value={input.length === 1 ? input[0] : input}
            title={t('panels.input')}
            testId="workflow-inspector-input-json"
          />
        ) : (
          <Empty>{t('panels.noInput')}</Empty>
        )}
      </Panel>
      <Panel title={failure ? t('panels.failure') : t('panels.result')}>
        {fromRunRecord && (
          <p className="text-muted-foreground mb-2 text-xs">{t('panels.fromRunRecord')}</p>
        )}
        {failure ? (
          <div className="space-y-2">
            <FailureChain failure={failure} />
            {hasResult && (
              <JsonViewer
                value={result}
                title={t('panels.result')}
                initialDepth={1}
                testId="workflow-inspector-result-json"
              />
            )}
          </div>
        ) : hasResult ? (
          <JsonViewer
            value={result}
            title={t('panels.result')}
            testId="workflow-inspector-result-json"
          />
        ) : (
          <Empty>{isRunning ? t('panels.resultPending') : t('panels.noResult')}</Empty>
        )}
      </Panel>
    </div>
  );
}

function Panel({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="min-w-0 space-y-1.5">
      <h3 className="text-foreground text-base font-semibold">{title}</h3>
      {children}
    </section>
  );
}

function Empty({ children }: { children: ReactNode }) {
  return (
    <p className="text-muted-foreground bg-background rounded-lg border p-4 text-sm">{children}</p>
  );
}
